import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useToast as useChakraToast, Box, HStack, VStack, Text, Icon, CloseButton } from '@chakra-ui/react';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';

// Status icons mapping
const statusIcons = {
  success: FaCheckCircle,
  error: FaExclamationCircle,
  warning: FaExclamationTriangle,
  info: FaInfoCircle,
};

// Status colors for the accent line and icon
const statusColors = {
  success: {
    accent: 'rgba(72, 187, 120, 0.95)',
    icon: '#38A169',
    bg: 'rgba(72, 187, 120, 0.2)',
  },
  error: {
    accent: 'rgba(245, 101, 101, 0.95)',
    icon: '#E53E3E',
    bg: 'rgba(245, 101, 101, 0.2)',
  },
  warning: {
    accent: 'rgba(237, 137, 54, 0.95)',
    icon: '#DD6B20',
    bg: 'rgba(237, 137, 54, 0.2)',
  },
  info: {
    accent: 'rgba(66, 153, 225, 0.95)',
    icon: '#3182CE',
    bg: 'rgba(66, 153, 225, 0.2)',
  },
};

// Custom toast component with glass-morphism effect and animations
const CustomToastComponent = ({ title, description, status = 'info', onClose, duration }) => {
  const [isExiting, setIsExiting] = useState(false);
  const containerRef = useRef(null);
  const colors = statusColors[status] || statusColors.info;
  const IconComponent = statusIcons[status] || FaInfoCircle;

  // Handle exit animation before closing
  const handleClose = useCallback(() => {
    setIsExiting(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      if (onClose) onClose();
    }, 350); // Match the CSS animation duration
  }, [onClose]);

  // Auto-close with exit animation
  useEffect(() => {
    if (duration) {
      const autoCloseTimer = setTimeout(() => {
        handleClose();
      }, duration - 350); // Start exit animation before duration ends

      return () => clearTimeout(autoCloseTimer);
    }
  }, [duration, handleClose]);

  return (
    <Box
      ref={containerRef}
      className={`custom-toast ${isExiting ? 'exiting' : ''}`}
      position="relative"
      minW="320px"
      maxW="420px"
      borderRadius="16px"
      overflow="hidden"
      // Glass-morphism effect - more transparent
      bg="rgba(255, 255, 255, 0.45)"
      backdropFilter="blur(20px) saturate(180%)"
      WebkitBackdropFilter="blur(20px) saturate(180%)"
      border="1px solid rgba(255, 255, 255, 0.25)"
      boxShadow="0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.05)"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.02) 100%)',
        pointerEvents: 'none',
      }}
    >
      {/* Accent line on the left */}
      <Box
        position="absolute"
        left={0}
        top={0}
        bottom={0}
        w="4px"
        bg={colors.accent}
        borderRadius="4px 0 0 4px"
      />
      
      <HStack
        spacing={3}
        p={4}
        pl={5}
        align="flex-start"
        position="relative"
        zIndex={1}
      >
        {/* Status icon with subtle background */}
        <Box
          p={2}
          borderRadius="full"
          bg={colors.bg}
          flexShrink={0}
        >
          <Icon
            as={IconComponent}
            color={colors.icon}
            boxSize={5}
          />
        </Box>

        {/* Content */}
        <VStack align="flex-start" spacing={0.5} flex={1} minW={0}>
          {title && (
            <Text
              fontWeight="600"
              fontSize="sm"
              color="gray.800"
              lineHeight="1.4"
            >
              {title}
            </Text>
          )}
          {description && (
            <Text
              fontSize="sm"
              color="gray.600"
              lineHeight="1.4"
              noOfLines={2}
            >
              {description}
            </Text>
          )}
        </VStack>

        {/* Close button */}
        <CloseButton
          size="sm"
          color="gray.500"
          _hover={{ color: 'gray.700', bg: 'rgba(0,0,0,0.06)' }}
          onClick={handleClose}
          borderRadius="full"
          flexShrink={0}
        />
      </HStack>
    </Box>
  );
};

// Custom hook that wraps Chakra's useToast with our custom styling
export const useCustomToast = () => {
  const chakraToast = useChakraToast();

  const toast = useCallback((options) => {
    const {
      title,
      description,
      status = 'info',
      duration = 3000,
      position = 'bottom-left',
      ...rest
    } = options;

    return chakraToast({
      position,
      duration: null, // We handle duration ourselves for the exit animation
      isClosable: false, // We handle close ourselves
      render: ({ onClose }) => (
        <CustomToastComponent
          title={title}
          description={description}
          status={status}
          onClose={onClose}
          duration={duration}
        />
      ),
      ...rest,
    });
  }, [chakraToast]);

  // Also expose closeAll for convenience
  toast.closeAll = chakraToast.closeAll;
  toast.close = chakraToast.close;
  toast.isActive = chakraToast.isActive;
  toast.update = chakraToast.update;

  return toast;
};

export default useCustomToast;
